import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, ListOrdered, ShoppingCart, User, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { AddUserIcon, SearchBtnIcon } from '@/components/agent-dashboard/assets';
import { TextInput } from '@/components/common/text-input';
import { TruncatedText } from '@/components/common/truncated-text';
import { Image } from '@/components/common/image';
import { CheckCircleActiveIcon, CheckIcon } from '../ui/icon';
import { CreateActorButton } from '../custom-digital-human';
import CheckboxGroup from '../common/check-box-group';
import { CreateAudioButton } from '../common/create-audio-button';
import { assetsVoiceHooks, useUserManagementHooks } from '@/manager';
import { RadioButton } from '../common/radio-button';
import { LoadingOverlay } from '../common/loading-overlay';
import { VoiceCard } from './voice-card';

interface VoiceAddModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  selectedId?: number;
  voices?: Audio[];
  loading?: boolean;
  selectedVoiceIds?: string[];
  onOpenChange?: (open: boolean) => void;
  onSave?: (voiceIds: string[]) => Promise<void>;
}

/** 添加声音弹窗 */
export const VoiceAddModal: React.FC<VoiceAddModalProps> = ({
  trigger,
  open: externalOpen,
  selectedId,
  voices = [],
  loading,
  selectedVoiceIds = [],
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation();

  const { voices: assetVoices, loading: assetVoicesLoading, refresh } = assetsVoiceHooks.useData();

  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref

  // 选中的声音列表
  const [selectedVoiceList, setSelectedVoiceList] = useState<string[]>([]);

  // 处理单个声音卡片的选择变化
  const handleVoiceSelect = (id: string, isSelected: boolean) => {
    setSelectedVoiceList(prev => {
      if (isSelected) {
        // 如果被选中且不存在，则添加到列表
        if (!prev.includes(id)) {
          return [...prev, id];
        }
      } else {
        // 如果被取消选中，则从列表中移除
        return prev.filter(voiceId => voiceId !== id);
      }
      return prev;
    });
  };

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  const handleSave = async () => {
    await onSave?.(selectedVoiceList);
    handleClose();
  }

  const reset = () => {
    setSelectedVoiceList([]);
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[958px] h-[680px]  flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-5">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <span>{t('agent_dashboard.user_detail.voice.add_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 flex flex-col px-6 select-none mb-5 overflow-hidden'>
          <div className='relative pt-0.5 pl-0.5 pr-0.5 flex-1 flex flex-col h-full overflow-y-auto'>
            <div className='grid grid-cols-3 gap-x-3 gap-y-5'>
              <CreateAudioButton generateType="Minimax" onSubmit={refresh} />
              {assetVoices.map((item, index) => (
                <VoiceCard
                  key={`${item.id}_${index}`}
                  added={selectedVoiceIds?.includes(item.id)}
                  name={item.name}
                  audioUrl={item.audioUrl}
                  id={item.id}
                  enableSelect
                  createdAt={item.createdAt}
                  isSelected={selectedVoiceList.includes(item.id)}
                  onSelect={handleVoiceSelect}
                />
              ))}
            </div>
            <LoadingOverlay loading={assetVoicesLoading} />
          </div>
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex items-center justify-end gap-3 p-6 z-20 select-none'>
          <TextButton
            label={t('agent_dashboard.user_detail.voice.add_modal.cancel')}
            className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
          <TextButton
            label={t('agent_dashboard.user_detail.voice.add_modal.save')}
            className='w-[135px] h-11'
            onClick={handleSave}
            loading={loading}
            disabled={loading || !selectedVoiceList.length}
          />
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
